import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { fireQuizConfetti } from "../components/Confetti";
import { toast } from "sonner";
import { errorMessage } from "../lib/errors";
import { Clock, AlertCircle, CheckCircle2, XCircle, ArrowRight, Trophy, RotateCcw } from "lucide-react";

export default function Quiz() {
  const { quizId } = useParams();
  const nav = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [tabSwitches, setTabSwitches] = useState(0);
  const startedAtRef = useRef(null);

  useEffect(() => {
    api.get(`/quizzes/${quizId}`).then(r => {
      setQuiz(r.data);
      setSeconds((r.data.duration_minutes || 60) * 60);
      startedAtRef.current = new Date().toISOString();
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || "Quiz indisponible");
      setTimeout(() => nav("/dashboard"), 1500);
    });
  }, [quizId, nav]);

  useEffect(() => {
    if (!quiz || submitted) return;
    const t = setInterval(() => setSeconds(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [quiz, submitted]);

  useEffect(() => {
    if (!quiz || submitted) return;
    const onVis = () => { if (document.hidden) setTabSwitches(c => c + 1); };
    const onCopy = (e) => { e.preventDefault(); toast.error("Copier-coller désactivé pendant le quiz"); };
    const onCtx = (e) => e.preventDefault();
    document.addEventListener("visibilitychange", onVis);
    document.addEventListener("copy", onCopy);
    document.addEventListener("cut", onCopy);
    document.addEventListener("contextmenu", onCtx);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("cut", onCopy);
      document.removeEventListener("contextmenu", onCtx);
    };
  }, [quiz, submitted]);

  const submit = async () => {
    if (submitted) return;
    try {
      const r = await api.post(`/quizzes/${quizId}/submit`, {
        answers, tab_switch_count: tabSwitches, started_at: startedAtRef.current,
      });
      setSubmitted(r.data);
      if (r.data.passed) fireQuizConfetti();
    } catch (e) { toast.error(errorMessage(e, "Erreur")); }
  };

  // Auto-submit at timer expiry — capture the latest `submit` via ref to avoid stale closure.
  const submitRef = useRef(submit);
  useEffect(() => { submitRef.current = submit; });
  useEffect(() => {
    if (quiz && seconds === 0 && !submitted) submitRef.current();
  }, [seconds, quiz, submitted]);

  if (!quiz) return <div className="p-10 text-center text-muted2 font-mono text-sm">Chargement…</div>;

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  const timerCls = seconds <= 300 ? "timer-danger" : seconds <= 900 ? "timer-warn" : "text-muted2";

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12" data-testid="quiz-result-page">
        <div className="glass rounded-3xl p-8 md:p-12 text-center">
          {submitted.passed ? (
            <>
              <Trophy className="w-16 h-16 text-gold-700 mx-auto mb-4 animate-fade-up" />
              <div className="text-xs uppercase tracking-widest text-gold-700 font-mono">Quiz validé</div>
              <h1 className="font-display text-4xl md:text-5xl text-ink-900 mt-2">Bravo, c'est validé ! 🎉</h1>
              <p className="text-muted2 mt-3">Vous avez prouvé que vous maîtrisez le sujet. Un cran de plus dans la cuirasse.</p>
            </>
          ) : (
            <>
              <div className="text-xs uppercase tracking-widest text-danger font-mono">Pas encore</div>
              <h1 className="font-display text-4xl md:text-5xl text-ink-900 mt-2">Pas tout à fait, mais on y est presque</h1>
              <p className="text-muted2 mt-3">70% étaient requis. Relisez les leçons, et retentez votre chance.</p>
            </>
          )}

          <div className="mt-6 inline-block px-6 py-3 rounded-full border border-gold-300 font-mono text-2xl text-gold-700-gradient" data-testid="quiz-score">
            {submitted.score_percent}%
          </div>

          <div className="text-left mt-8 space-y-3" data-testid="quiz-breakdown">
            {submitted.breakdown.map((b, i) => (
              <details key={b.question_id || `q-${i}`} className={`border rounded-xl p-4 ${b.is_correct ? "border-success/40 bg-success/5" : "border-danger/40 bg-danger/5"}`}>
                <summary className="cursor-pointer flex items-start gap-2 text-sm">
                  {b.is_correct ? <CheckCircle2 className="w-4 h-4 text-success mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-danger mt-0.5 flex-shrink-0" />}
                  <span className="flex-1 text-ink-800">{i+1}. {b.question_text}</span>
                </summary>
                <div className="mt-3 text-sm text-muted2 pl-6 space-y-1">
                  <div>Votre réponse : <span className={b.is_correct ? "text-success" : "text-danger"}>{b.user_answer || "(vide)"}</span></div>
                  {!b.is_correct && <div>Bonne réponse : <span className="text-gold-700">{b.correct_answer}</span></div>}
                  <div className="italic mt-2">{b.explanation}</div>
                </div>
              </details>
            ))}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            {submitted.passed && submitted.all_quizzes_passed && (
              <button onClick={() => nav(`/payment/${submitted.attempt.certification_id ? "" : ""}`) || nav("/dashboard")} className="px-6 py-3 rounded-full bg-gold-500 text-ink-900 font-medium hover:bg-gold-500-400 shadow-gold inline-flex items-center gap-2">
                Obtenir mon certificat <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {!submitted.passed && (
              <button onClick={() => window.location.reload()} className="px-6 py-3 rounded-full border border-gold-300 hover:bg-gold-500/10 inline-flex items-center gap-2" data-testid="quiz-retry-button">
                <RotateCcw className="w-4 h-4" /> Réessayer
              </button>
            )}
            <button onClick={() => nav("/dashboard")} className="px-6 py-3 rounded-full border border-ink-200 hover:bg-white/5">Retour au tableau de bord</button>
          </div>
        </div>
      </div>
    );
  }

  const answered = Object.keys(answers).length;
  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="quiz-page">
      <div className="sticky top-16 z-30 glass rounded-2xl px-5 py-4 flex items-center justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-gold-700 font-mono">Quiz</div>
          <div className="font-display text-lg text-ink-900">{quiz.title}</div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted2">{answered}/{quiz.questions.length}</span>
          <span className={`font-mono text-2xl ${timerCls} inline-flex items-center gap-1.5`} data-testid="quiz-timer">
            <Clock className="w-5 h-5" /> {mm}:{ss}
          </span>
        </div>
      </div>

      {tabSwitches > 0 && (
        <div className="bg-warning/10 border border-warning/30 text-warning rounded-lg px-4 py-2 mb-4 text-xs flex gap-2 items-center" data-testid="tab-switch-warning">
          <AlertCircle className="w-4 h-4" /> {tabSwitches} changement(s) d'onglet détecté(s). Restez sur cette page pour valider votre tentative.
        </div>
      )}

      <div className="space-y-4">
        {quiz.questions.map((q, i) => (
          <div key={q.id} className="glass rounded-2xl p-5" data-testid={`question-${i+1}`}>
            <div className="text-xs text-gold-700 font-mono uppercase tracking-widest mb-2">Question {i+1}</div>
            <div className="text-ink-900 text-base md:text-lg">{q.question_text}</div>
            {q.image_url && <img src={q.image_url} alt="" className="rounded-lg mt-3 max-h-60" />}
            <div className="mt-4 grid gap-2">
              {(q.options || []).map(opt => (
                <label key={opt} className={`px-4 py-3 border rounded-lg cursor-pointer transition-all flex items-center gap-3 ${
                  answers[q.id] === opt ? "border-gold bg-gold-500/10" : "border-ink-200 hover:border-gold-300"
                }`}>
                  <input type="radio" name={q.id} value={opt}
                    checked={answers[q.id] === opt}
                    onChange={() => setAnswers({ ...answers, [q.id]: opt })}
                    className="accent-[#C9A84C]"
                    data-testid={`answer-${i+1}-${(q.options || []).indexOf(opt)}`}
                  />
                  <span className="text-sm text-ink-800">{opt}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-3">
        <span className="text-xs text-muted2">Tentative {quiz.attempts_used + 1} / 3 · score min. {quiz.passing_score_percent}%</span>
        <button onClick={submit} className="px-7 py-3 rounded-full bg-gold-500 text-ink-900 font-medium hover:bg-gold-500-400 shadow-gold inline-flex items-center gap-2" data-testid="quiz-submit-button">
          Soumettre mes réponses <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
